/**
 * 四象限表格组件
 * 用于 Sub-topic Fermentation Analysis 的表格视图
 */
import React, { useMemo, useState } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_V } from '@op/utils/format';
import { getFilterDateQOQText } from '@op/utils/time';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import { QUADRANT_CONFIG, CATEGORY_CONFIG } from '../../constants';
import type { IProcessedQuadrantItem } from '../../hooks/useQuadrantData';
import './QuadrantTable.scss';
import { SortBy } from 'tea-component/lib/table/addons';

interface IQuadrantTableProps {
  data: IProcessedQuadrantItem[];
  filter: any;
  maxHeight?: number;
  categoryCnMap: Map<string, string>;
}

const QuadrantTable: React.FC<IQuadrantTableProps> = ({
  data = [],
  filter,
  maxHeight = 450,
  categoryCnMap,
}) => {
  const { formatMessage, isEn } = useLanguage();
  const { text: qoqText } = useMemo(() => {
    return getFilterDateQOQText(filter);
  }, [filter]);
  const sortState = useState<SortBy>({ by: 'mentions', order: 'desc' });

  const { tableColumns, sortColumns } = useMemo(() => {
    const columns: any[] = [
      {
        key: 'category',
        header: formatMessage({ id: 'Category' }),
        align: 'left',
        fixed: 'left',
        render: (row: IProcessedQuadrantItem) => {
          const config = CATEGORY_CONFIG[row.category];
          const color = config?.color || '#1890ff';
          const categoryText = isEn
            ? row.category
            : categoryCnMap.get(row.category) || row.category;
          return (
            <div className="dbo-quadrant-table__report-type">
              <span
                className="dbo-quadrant-table__report-type-dot"
                style={{ background: color }}
              />
              <span>{categoryText}</span>
            </div>
          );
        },
      },
      {
        key: 'sub_category',
        header: formatMessage({ id: 'Subtopic' }),
        width: 240,
        align: 'left',
        fixed: 'left',
        render: (row: IProcessedQuadrantItem) => (
          <span className="dbo-quadrant-table__topic-link">
            {isEn ? row.sub_category : row.sub_category_cn || row.sub_category}
          </span>
        ),
      },
      {
        key: 'mentions',
        header: formatMessage({ id: 'Mentions' }),
        align: 'right',
        render: (row: IProcessedQuadrantItem) => DBO_V.reviews(row.mentions),
      },
      {
        key: 'avg_engagement_intensity',
        header: formatMessage({ id: 'Avg Engagement' }),
        align: 'right',
        render: (row: IProcessedQuadrantItem) =>
          row.avg_engagement_intensity?.toFixed(2) || '-',
      },
      {
        key: 'change_rate',
        header: formatMessage({ id: qoqText }),
        align: 'right',
        render: (row: IProcessedQuadrantItem) => (
          <PercentageItem percentage={row.change_rate * 100} />
        ),
      },
      {
        key: 'quadrant',
        header: formatMessage({ id: 'Risk Type' }),
        align: 'left',
        render: (row: IProcessedQuadrantItem) => {
          const quadrant = row.quadrant || 4;
          const config = QUADRANT_CONFIG[quadrant];
          return (
            <span style={{ color: config?.labelColor }}>
              {formatMessage({ id: config?.name || '' })}
            </span>
          );
        },
      },
    ];

    return {
      tableColumns: columns,
      sortColumns: ['mentions', 'avg_engagement_intensity', 'change_rate'],
    };
  }, [formatMessage, qoqText, isEn, categoryCnMap]);

  return (
    <TableCommonHook
      recordKey={(item) => item.category + item.sub_category}
      className="dbo-quadrant-table-wrapper"
      columns={tableColumns}
      total={data?.length}
      records={data}
      hasPageable={false}
      maxHeight={maxHeight}
      sortColumns={sortColumns}
      sortInfo={sortState}
      sortType="local"
    />
  );
};

export default QuadrantTable;
